import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/Toast';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../i18n/useAppLocale';
import { getWorkHappinessFeedbacksByParameters, getFurtherFeelingsFeedbacksByParameters } from '../../../services/statistics/happinessReport';
import { getInsightsByParameters } from '../../../services/statistics/insights';
import { searchGroups } from '../../../services/group/group';
import debounce from 'debounce-promise';
import surveyConfigQuestionsNew from '../../../data/questions-ibfoz-jan26.json';
import surveyConfigQuestionsOld from '../../../data/questions-ifoz-jan23--dez25.json';
import { representationAgeRangeOptions, representationSexOptions, representationOptionsValues } from '../../../services/utils/representationOptions';
import { DimensionFormattedName, DimensionID } from '../../../enums/dimension';
import { UserRole } from '../../../enums/user';
import { shouldBypassStatisticsCacheForSuperAdmin } from '../../../services/statistics/standardStatisticsCacheEligibility';
import { LineChartSeriesInterface } from '../../../interfaces/chartSeries.interface';
import { InsightsMonthData } from '../../../interfaces/insights.interface';
import { FeedbacksPageResults } from '../../../interfaces/feedback.interface';
import { FormattedDimensionResults } from '../../../interfaces/dimension.interface';
import { getQuestionnaireVersion } from '../../../i18n/questionnaire';

dayjs.extend(timezone);

const getQuestionsForYearMonth = (year?: string | number, month?: string | number) => {
  const parsedYear = Number(year);
  const parsedMonth = Number(month);

  if (!parsedYear || !parsedMonth) {
    return surveyConfigQuestionsNew;
  }

  const version = getQuestionnaireVersion(`${parsedYear}-${String(parsedMonth).padStart(2, '0')}`);
  return version === 'historical' ? surveyConfigQuestionsOld : surveyConfigQuestionsNew;
};


const dimensionsIdList = Object.values(DimensionID).filter((v) => typeof v === 'number') as DimensionID[];

const getRepresentationLabelByValue = (targetValue: number | number[]) => {
  const foundEntry = Object.entries(representationOptionsValues).find(([, value]) => {
    if (typeof targetValue === 'number') {
      return value === targetValue;
    }

    return Array.isArray(value) && value.length === targetValue.length && value.every((item, index) => item === targetValue[index]);
  });

  return foundEntry?.[0];
};

const toNumericValue = (value: unknown) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
};

const buildFormattedDimensionResults = (
  dimensionsResults: Record<string, any> | undefined,
  yearToUse: string,
  monthToUse: string,
  forceEmptyValues = false
): FormattedDimensionResults[] => {
  const surveyQuestions = getQuestionsForYearMonth(yearToUse, monthToUse);

  return dimensionsIdList.map((dimensionId) => {
    const dimensionQuestions = surveyQuestions.filter((question: any) => question.dimension === DimensionFormattedName[dimensionId].toUpperCase());
    const apiQuestions = dimensionsResults?.[dimensionId]?.questions ?? [];
    const apiQuestionsMap = new Map(apiQuestions.map((question: any) => [Number(question.questionId), question.ifoz]));

    return {
      dimensionId,
      questionsResults: dimensionQuestions.map((question: any) => ({
        id: question.id,
        question: question.question,
        value: forceEmptyValues ? null : toNumericValue(apiQuestionsMap.get(Number(question.id)))
      })),
      totalIfoz: forceEmptyValues ? null : toNumericValue(dimensionsResults?.[dimensionId]?.dimensionTotal?.ifoz)
    };
  });
};

const buildComparativeSeries = (
  items: Record<string, any> | undefined,
  yearToUse: string,
  monthToUse: string,
  labelResolver: (key: string, item: any) => string,
  seriesFormatter?: (item: any, dimensions: FormattedDimensionResults[]) => Partial<LineChartSeriesInterface>
) => {
  return Object.entries(items ?? {}).map(([key, item]) => {
    const dimensions = buildFormattedDimensionResults(item?.dimensionsResults, yearToUse, monthToUse, item?.hasEnoughSample === false);

    return {
      label: labelResolver(key, item),
      data: dimensions,
      ifoz: toNumericValue(item?.ifoz),
      hasEnoughSample: item?.hasEnoughSample ?? null,
      sampleSize: item?.sampleSize ?? null,
      ...seriesFormatter?.(item, dimensions)
    } as LineChartSeriesInterface;
  });
};

/**
 * Hook que centraliza toda a lógica do Relatório de Acompanhamento.
 * - Mantém estados (loading, dados gerais, dados filtrados, series)
 * - Executa chamadas aos serviços (insights, feedbacks, grupos)
 * - Expõe handlers para navegação de datas, aplicação de filtros e paginação
 *
 * OBS:
 * - A lógica de data (mês anterior se dia < 20) foi mantida.
 * - O tratamento de erro e a lógica de retry com localStorage foram preservados.
 */

// Tipos locais para os modelos de dados do relatório
type GeneralDataModel = {
  generalIap: number | null;
  generalIfoz: number | null;
  generalIep: number | null;
  generalIfcz: number | null;
  generalEpDimension: FormattedDimensionResults | null;
  generalEngDimension: FormattedDimensionResults | null;
  generalVitDimension: FormattedDimensionResults | null;
  generalRelDimension: FormattedDimensionResults | null;
  generalSigDimension: FormattedDimensionResults | null;
  generalReaDimension: FormattedDimensionResults | null;
};

type FilteredDataModel = {
  filteredIap: number | null;
  filteredIfoz: number | null;
  filteredIep: number | null;
  filteredIfcz: number | null;
  filteredEpDimension: FormattedDimensionResults | null;
  filteredEngDimension: FormattedDimensionResults | null;
  filteredVitDimension: FormattedDimensionResults | null;
  filteredRelDimension: FormattedDimensionResults | null;
  filteredSigDimension: FormattedDimensionResults | null;
  filteredReaDimension: FormattedDimensionResults | null;
};

type FilterOverrides = {
  representationSelectedOptions?: string[];
  selectedGroupOptions?: string[];
};

export const useHappinessReportData = () => {
  const { t } = useTranslation('reports');
  const appLocale = useAppLocale();
  // Contexts
  const { user, SignOut, saveRetryingFlagOnStorage, deleteRetryingFlagOnStorage } = useAuth();
  const { showToast } = useToast();
  const companyCreatedAt = user.company_visited?.created_at ?? null;

  // Estados de Loading
  const [loading, setLoading] = useState(true);
  const [loadingWorkHappinessFeedbacksData, setLoadingWorkHappinessFeedbacksData] = useState(true);
  const [loadingFurtherFeelingsFeedbacksData, setLoadingFurtherFeelingsFeedbacksData] = useState(true);
  const [loadingSexesData, setLoadingSexesData] = useState(true);
  const [loadingAgeRangesData, setLoadingAgeRangesData] = useState(true);
  const [loadingGroupsData, setLoadingGroupsData] = useState(true);

  // Dados para os gráficos
  const [dimensionsByFilters, setDimensionsByFilters] = useState<LineChartSeriesInterface[]>([]);
  const [genderSeries, setGenderSeries] = useState<LineChartSeriesInterface[]>([]);
  const [ageRangesSeries, setAgeRangesSeries] = useState<LineChartSeriesInterface[]>([]);
  const [groupsSeries, setGroupsSeries] = useState<LineChartSeriesInterface[]>([]);

  // Dados gerais e filtrados
  const [generalData, setGeneralData] = useState<GeneralDataModel>({
    generalIap: null,
    generalIfoz: null,
    generalIep: null,
    generalIfcz: null,
    generalEpDimension: null,
    generalEngDimension: null,
    generalVitDimension: null,
    generalRelDimension: null,
    generalSigDimension: null,
    generalReaDimension: null
  });

  const [filteredData, setFilteredData] = useState<FilteredDataModel>({
    filteredIap: null,
    filteredIfoz: null,
    filteredIep: null,
    filteredIfcz: null,
    filteredEpDimension: null,
    filteredEngDimension: null,
    filteredVitDimension: null,
    filteredRelDimension: null,
    filteredSigDimension: null,
    filteredReaDimension: null
  });

  // Feedbacks com paginação
  const [workHappinessFeedbacksPageResults, setWorkHappinessFeedbacksPageResults] = useState<FeedbacksPageResults>({
    total_items: 0, total_pages: 0, current_page: 1, data: []
  });
  const [furtherFeelingsFeedbacksPageResults, setFurtherFeelingsFeedbacksPageResults] = useState<FeedbacksPageResults>({
    total_items: 0, total_pages: 0, current_page: 1, data: []
  });

  // Controles de data e UI
  const [dateValue, setDateValue] = useState<string>('');
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [datePickerIsOpened, setDatePickerIsOpened] = useState(false);

  // Filtros
  const [representationSelectedOptions, setRepresentationSelectedOptions] = useState<string[]>([]);
  const [selectGroupOptions, setSelectGroupOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedGroupOptions, setSelectedGroups] = useState<string[]>([]);
  const [appliedRepresentationSelectedOptions, setAppliedRepresentationSelectedOptions] = useState<string[]>([]);
  const [appliedGroupOptions, setAppliedGroupOptions] = useState<string[]>([]);
  const dataRequestIdRef = useRef(0);
  const [representationFilterHasChanged, setRepresentationFilterHasChanged] = useState(false);
  const [groupFilterHasChanged, setGroupFilterHasChanged] = useState(false);

  // Permissões e IDs de grupos do usuário
  const isGroupResponsible = user?.user?.role === UserRole.group_responsible;
  const shouldBypassStatisticsCache = shouldBypassStatisticsCacheForSuperAdmin(user?.user?.role);
  const responsibleGroupIds: string[] = isGroupResponsible && Array.isArray(user?.user?.responsible_groups)
    ? user.user.responsible_groups : [];

  /* ---------- Helpers ---------- */
  const renderDate = (date?: string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString(appLocale, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const clearFilteredData = () => {
    setFilteredData({
      filteredIap: null, filteredIfoz: null, filteredIep: null, filteredIfcz: null,
      filteredEpDimension: null, filteredEngDimension: null, filteredVitDimension: null,
      filteredRelDimension: null, filteredSigDimension: null, filteredReaDimension: null
    });
  };

  // Carrega as opções de grupos para o filtro
  const loadGroupInputOptions = async (term?: string) => {
    try {
      const company = user.company_visited?.id;
      if (!company) return [];

      const params: any = { company_id: company, groupsStatuses: [1], detailedMembersData: false };
      if (user.user?.role === UserRole.group_responsible) params.responsibleUsers = [user.user?.id];

      const result: any = await searchGroups(params);
      if (!result?.data?.length) return [];
      const options = result.data.map((g: any) => ({ label: g.name, value: g.id }));
      options.sort((a: any, b: any) => a.label.localeCompare(b.label, appLocale));
      return options;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const debouncedLoadGroupInputOptions = debounce(loadGroupInputOptions, 700);

  // Carrega feedbacks de "Felicidade no Trabalho"
  const loadWorkHappinessFeedbacks = async (page: number, year?: string, month?: string, filters?: any, useStandardCache = false) => {
    setLoadingWorkHappinessFeedbacksData(true);
    const company = user.company_visited?.id;
    if (!company) throw new Error('company_not_found');

    // Lógica de data padrão mantida
    const now = new Date();
    let currentYear = now.getFullYear();
    let currentMonth = now.getMonth() + 1;
    if (now.getDate() < 20) {
      currentMonth -= 1;
      if (currentMonth === 0) { currentMonth = 12; currentYear -= 1; }
    }
    const yearToUse = year || String(currentYear);
    const monthToUse = month || String(currentMonth).padStart(2, '0');

    try {
      const data = await getWorkHappinessFeedbacksByParameters(company, monthToUse, yearToUse, `${page}`, filters, useStandardCache && !shouldBypassStatisticsCache ? {
        enabled: true,
        userId: user.user?.id,
        scope: 'happiness-report:work-feedbacks'
      } : undefined);
      setWorkHappinessFeedbacksPageResults({
        total_items: data.total_items,
        total_pages: data.total_pages,
        current_page: parseInt(data.current_page),
        data: data.data ?? []
      });
      setLoadingWorkHappinessFeedbacksData(false);
    } catch (e) {
      setLoadingWorkHappinessFeedbacksData(false);
      throw e;
    }
  };

  // Carrega feedbacks de "Sentindo algo mais?"
  const loadFurtherFeelingsFeedbacks = async (page: number, year?: string, month?: string, filters?: any, useStandardCache = false) => {
    setLoadingFurtherFeelingsFeedbacksData(true);
    const company = user.company_visited?.id;
    if (!company) throw new Error('company_not_found');

    const now = new Date();
    let currentYear = now.getFullYear();
    let currentMonth = now.getMonth() + 1;
    if (now.getDate() < 20) {
      currentMonth -= 1;
      if (currentMonth === 0) { currentMonth = 12; currentYear -= 1; }
    }
    const yearToUse = year || String(currentYear);
    const monthToUse = month || String(currentMonth).padStart(2, '0');

    try {
      const data = await getFurtherFeelingsFeedbacksByParameters(company, monthToUse, yearToUse, `${page}`, filters, useStandardCache && !shouldBypassStatisticsCache ? {
        enabled: true,
        userId: user.user?.id,
        scope: 'happiness-report:further-feedbacks'
      } : undefined);
      setFurtherFeelingsFeedbacksPageResults({
        total_items: data.total_items,
        total_pages: data.total_pages,
        current_page: parseInt(data.current_page),
        data: data.data ?? []
      });
      setLoadingFurtherFeelingsFeedbacksData(false);
    } catch (e) {
      setLoadingFurtherFeelingsFeedbacksData(false);
      throw e;
    }
  };

  const loadComparativeData = async ({
    companyId,
    monthToUse,
    yearToUse,
    filters,
    groupOptions,
    useStandardCache = false,
    requestId
  }: {
    companyId: string;
    monthToUse: string;
    yearToUse: string;
    filters: { sexes: number[]; ageRanges: number[][]; groups: string[] };
    groupOptions: { label: string; value: string }[];
    useStandardCache?: boolean;
    requestId?: number;
  }) => {
    setLoadingSexesData(true);
    setLoadingAgeRangesData(true);
    setLoadingGroupsData(true);

    try {
      const allSexIds = representationSexOptions
        .map((key) => representationOptionsValues[key])
        .filter((value): value is number => typeof value === 'number');
      const allAgeRanges = representationAgeRangeOptions
        .map((key) => representationOptionsValues[key])
        .filter((value): value is number[] => Array.isArray(value));

      const sexesToShow = filters.sexes.length > 0 ? filters.sexes : allSexIds;
      const ageRangesToShow = filters.ageRanges.length > 0 ? filters.ageRanges : allAgeRanges;
      const groupsToShow = filters.groups.length > 0 ? filters.groups : groupOptions.map((item) => item.value);

      const comparativeResults: InsightsMonthData[] = await getInsightsByParameters(companyId, monthToUse, yearToUse, 1, {
        sexes: sexesToShow,
        ageRanges: ageRangesToShow,
        groups: groupsToShow,
        separateDataByEachFilter: true,
        separateDataContextMode: 'contextual',
        responseScope: {
          generalData: false,
          filteredDataCombined: false,
          filteredData: {
            sexes: {
              metrics: ['ifoz', 'dimensions', 'questions']
            },
            ageRanges: {
              metrics: ['ifoz', 'dimensions', 'questions']
            },
            groups: {
              metrics: ['ifoz', 'dimensions', 'questions']
            }
          }
        }
      }, useStandardCache && !shouldBypassStatisticsCache ? {
        enabled: true,
        userId: user.user?.id,
        scope: 'happiness-report:comparative'
      } : undefined);

      if (requestId !== undefined && requestId !== dataRequestIdRef.current) return;

      const comparativeMonth = comparativeResults[0] ?? {};
      const comparativeFilteredData = comparativeMonth.filteredData ?? {};
      const selectedGroupMap = new Map(groupOptions.map((group) => [group.value, group.label]));

      setGenderSeries(buildComparativeSeries(
        comparativeFilteredData.sexes,
        yearToUse,
        monthToUse,
        (sexId) => {
          const sexValue = Number(sexId);
          return getRepresentationLabelByValue(sexValue) ?? `Sexo ${sexId}`;
        }
      ));

      setAgeRangesSeries(buildComparativeSeries(
        comparativeFilteredData.ageRanges,
        yearToUse,
        monthToUse,
        (rangeKey) => {
          const rangeLabel = Object.entries(representationOptionsValues).find(([, value]) => {
            return Array.isArray(value) && value.join('-') === rangeKey;
          })?.[0];

          return rangeLabel ?? rangeKey;
        }
      ));

      setGroupsSeries(buildComparativeSeries(
        comparativeFilteredData.groups,
        yearToUse,
        monthToUse,
        (groupId) => selectedGroupMap.get(groupId) ?? t('groupFallback', { id: groupId })
      ));
    } finally {
      if (requestId !== undefined && requestId !== dataRequestIdRef.current) return;
      setLoadingSexesData(false);
      setLoadingAgeRangesData(false);
      setLoadingGroupsData(false);
    }
  };

  // Fun??o principal para carregar os dados
  const loadData = async (yearSelected?: string, monthSelected?: string, requestId = ++dataRequestIdRef.current) => {
    try {
      if (requestId !== dataRequestIdRef.current) return;
      setLoading(true);

      const now = new Date();
      let currentYear = now.getFullYear();
      let currentMonth = now.getMonth() + 1;
      if (now.getDate() < 20) {
        currentMonth -= 1;
        if (currentMonth === 0) { currentMonth = 12; currentYear -= 1; }
      }
      const yearToUse = yearSelected || String(currentYear);
      const monthToUse = monthSelected || String(currentMonth).padStart(2, '0');

      const companyId = user.company_visited?.id;
      if (!companyId) throw new Error('company_not_found');

      const groupOptions = await loadGroupInputOptions() as { label: string, value: string }[];
      setSelectGroupOptions(groupOptions);

      const insightsResults: InsightsMonthData[] = await getInsightsByParameters(companyId, monthToUse, yearToUse, 1, {
        sexes: [],
        ageRanges: [],
        groups: [],
        separateDataByEachFilter: false,
        responseScope: {
          generalData: {
            metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions', 'questions']
          }
        }
      }, !shouldBypassStatisticsCache ? {
        enabled: true,
        userId: user.user?.id,
        scope: 'happiness-report:general'
      } : undefined);

      const currentMonthData = insightsResults[0];
      const generalDimensions = buildFormattedDimensionResults(currentMonthData?.generalData?.dimensionsResults, yearToUse, monthToUse);

      if (requestId !== dataRequestIdRef.current) return;
      setGeneralData({
        generalIap: currentMonthData?.generalData?.iap ?? null,
        generalIfoz: currentMonthData?.generalData?.ifoz ?? null,
        generalIep: currentMonthData?.generalData?.iep ?? null,
        generalIfcz: currentMonthData?.generalData?.ifcz ?? null,
        generalEpDimension: generalDimensions[DimensionID.POSITIVE_EMOTIONS - 1],
        generalEngDimension: generalDimensions[DimensionID.ENGAGEMENT - 1],
        generalRelDimension: generalDimensions[DimensionID.RELATIONSHIP - 1],
        generalSigDimension: generalDimensions[DimensionID.MEANING - 1],
        generalReaDimension: generalDimensions[DimensionID.ACCOMPLISHMENT - 1],
        generalVitDimension: generalDimensions[DimensionID.VITALITY - 1]
      });

      await loadComparativeData({
        companyId,
        monthToUse,
        yearToUse,
        filters: { sexes: [], ageRanges: [], groups: [] },
        groupOptions,
        useStandardCache: !shouldBypassStatisticsCache,
        requestId
      });

      if (requestId !== dataRequestIdRef.current) return;
      setAppliedRepresentationSelectedOptions([]);
      setAppliedGroupOptions([]);
      setLoading(false);
    } catch (error: any) {
      if (requestId !== dataRequestIdRef.current) return;
      setRepresentationSelectedOptions(appliedRepresentationSelectedOptions);
      setSelectedGroups(appliedGroupOptions);
      setLoading(false);
      console.error(error);

      if (error?.response?.status === 401) {
        showToast(t('sessionExpired'), 'info', 7500);
        setTimeout(() => {
          showToast(t('redirecting'), 'info', 5000);
          setTimeout(() => SignOut('/admin/signup'), 5000);
        }, 2500);
      } else if (error?.response?.status === 403) {
        showToast(t('permission'), 'error', 30000);
      } else if (error?.response?.data?.error) {
        switch (error.response.data.error) {
          case 'Erro ao validar o token - zenbox_backend':
            try {
              const retryingFlag = localStorage.getItem('zenbox.retryingFlag');
              if (retryingFlag && JSON.parse(retryingFlag)?.isRetrying) {
                throw new Error('Retry j? tentado');
              } else {
                saveRetryingFlagOnStorage({ isRetrying: true });
                const now = new Date();
                let currentYear = now.getFullYear();
                let currentMonth = now.getMonth() + 1;
                if (now.getDate() < 20) { currentMonth -= 1; if (currentMonth === 0) { currentMonth = 12; currentYear -= 1; } }
                const yearToUse = currentYear.toString();
                const monthToUse = String(currentMonth).padStart(2, '0');
                await loadData(yearToUse, monthToUse);
              }
              deleteRetryingFlagOnStorage();
            } catch (e) {
              showToast(t('sessionExpired'), 'info', 7500);
              setTimeout(() => {
                showToast(t('redirecting'), 'info', 5000);
                setTimeout(() => SignOut('/admin/signup'), 5000);
              }, 2500);
            }
            break;
          default:
            showToast(t('genericError'), 'error', 30000);
            throw error;
        }
      } else {
        showToast(t('genericError'), 'error', 30000);
        throw error;
      }
    }
  };

  // Carrega dados com base nos filtros selecionados
  const loadFilteredData = async (year?: string, month?: string, overrides?: FilterOverrides) => {
    const requestId = ++dataRequestIdRef.current;
    const representationsToFilter = overrides?.representationSelectedOptions ?? representationSelectedOptions;
    const groupsToFilter = overrides?.selectedGroupOptions ?? selectedGroupOptions;
    const thereIsSomeFilter = (representationsToFilter?.length > 0) || (groupsToFilter?.length > 0);

    if (!thereIsSomeFilter) {
      setDimensionsByFilters([]);
      await loadData(year, month, requestId);
      if (requestId !== dataRequestIdRef.current) return;
      clearFilteredData();
      await Promise.all([
        loadWorkHappinessFeedbacks(1, year, month, undefined, true),
        loadFurtherFeelingsFeedbacks(1, year, month, undefined, true),
      ]);
      if (requestId === dataRequestIdRef.current) {
        setAppliedRepresentationSelectedOptions([]);
        setAppliedGroupOptions([]);
      }
      return;
    }

    try {
      setLoading(true);

      const now = new Date();
      let currentYear = now.getFullYear();
      let currentMonth = now.getMonth() + 1;
      if (now.getDate() < 20) {
        currentMonth -= 1;
        if (currentMonth === 0) { currentMonth = 12; currentYear -= 1; }
      }
      const yearToUse = year || currentYear.toString();
      const monthToUse = month || currentMonth.toString().padStart(2, '0');

      const companyId = user.company_visited?.id;
      if (!companyId) throw new Error('company_not_found');

      const selectedSexes = representationsToFilter
        .map((key) => representationOptionsValues[key])
        .filter((value): value is number => typeof value === 'number');
      const selectedAgeRanges = representationsToFilter
        .map((key) => representationOptionsValues[key])
        .filter((value): value is number[] => Array.isArray(value));

      const insightsResults: InsightsMonthData[] = await getInsightsByParameters(companyId, monthToUse, yearToUse, 1, {
        sexes: selectedSexes,
        ageRanges: selectedAgeRanges,
        groups: groupsToFilter,
        separateDataByEachFilter: false,
        responseScope: {
          generalData: {
            metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions', 'questions']
          },
          filteredDataCombined: {
            metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions', 'questions']
          }
        }
      });

      if (requestId !== dataRequestIdRef.current) return;

      const currentMonthData = insightsResults[0];
      const generalDimensions = buildFormattedDimensionResults(currentMonthData?.generalData?.dimensionsResults, yearToUse, monthToUse);
      const filteredDimensions = buildFormattedDimensionResults(currentMonthData?.filteredDataCombined?.dimensionsResults, yearToUse, monthToUse);

      setGeneralData({
        generalIap: currentMonthData?.generalData?.iap ?? null,
        generalIfoz: currentMonthData?.generalData?.ifoz ?? null,
        generalIep: currentMonthData?.generalData?.iep ?? null,
        generalIfcz: currentMonthData?.generalData?.ifcz ?? null,
        generalEpDimension: generalDimensions[DimensionID.POSITIVE_EMOTIONS - 1],
        generalEngDimension: generalDimensions[DimensionID.ENGAGEMENT - 1],
        generalRelDimension: generalDimensions[DimensionID.RELATIONSHIP - 1],
        generalSigDimension: generalDimensions[DimensionID.MEANING - 1],
        generalReaDimension: generalDimensions[DimensionID.ACCOMPLISHMENT - 1],
        generalVitDimension: generalDimensions[DimensionID.VITALITY - 1]
      });

      setFilteredData((previous) => ({
        ...previous,
        filteredIfoz: currentMonthData?.filteredDataCombined?.ifoz ?? null,
        filteredIap: currentMonthData?.filteredDataCombined?.iap ?? null,
        filteredIep: currentMonthData?.filteredDataCombined?.iep ?? null,
        filteredIfcz: currentMonthData?.filteredDataCombined?.ifcz ?? null,
        filteredEpDimension: filteredDimensions[DimensionID.POSITIVE_EMOTIONS - 1],
        filteredEngDimension: filteredDimensions[DimensionID.ENGAGEMENT - 1],
        filteredRelDimension: filteredDimensions[DimensionID.RELATIONSHIP - 1],
        filteredSigDimension: filteredDimensions[DimensionID.MEANING - 1],
        filteredReaDimension: filteredDimensions[DimensionID.ACCOMPLISHMENT - 1],
        filteredVitDimension: filteredDimensions[DimensionID.VITALITY - 1]
      }));

      await Promise.all([
        loadWorkHappinessFeedbacks(1, yearToUse, monthToUse, { sexes: selectedSexes, ageRanges: selectedAgeRanges, groups: groupsToFilter }),
        loadFurtherFeelingsFeedbacks(1, yearToUse, monthToUse, { sexes: selectedSexes, ageRanges: selectedAgeRanges, groups: groupsToFilter })
      ]);

      if (requestId !== dataRequestIdRef.current) return;

      await loadComparativeData({
        companyId,
        monthToUse,
        yearToUse,
        filters: {
          sexes: selectedSexes,
          ageRanges: selectedAgeRanges,
          groups: groupsToFilter.length > 0 ? groupsToFilter : selectGroupOptions.map((group) => group.value)
        },
        groupOptions: selectGroupOptions,
        requestId
      });

      setAppliedRepresentationSelectedOptions([...representationsToFilter]);
      setAppliedGroupOptions([...groupsToFilter]);
      setLoading(false);
    } catch (error: any) {
      if (requestId !== dataRequestIdRef.current) return;
      setRepresentationSelectedOptions(appliedRepresentationSelectedOptions);
      setSelectedGroups(appliedGroupOptions);
      setLoading(false);
      console.error(error);
      throw error;
    }
  };
  // Handler para mudança de data
  const loadDataByDate = async (date: any) => {
    setDateValue(date);
    if (!date) {
      await loadData();
      return;
    }
    const [year, month] = date.split('-');
    const representationsToFilter = representationSelectedOptions;
    const groupsToFilter = selectedGroupOptions;

    if (!(representationsToFilter?.length > 0) && !(groupsToFilter?.length > 0)) {
      await loadData(year, month);
      await Promise.all([
        loadWorkHappinessFeedbacks(1, year, month, undefined, true),
        loadFurtherFeelingsFeedbacks(1, year, month, undefined, true)
      ]);
      clearFilteredData();
    } else {
      await loadFilteredData(year, month);
    }
  };

  // Handlers para navegação de mês
  const handleNextMonth = async () => {
    const formattedDate = dayjs(dateValue).tz(clientTimezone).add(1, 'month').format('YYYY-MM');
    setDateValue(formattedDate);
    await loadDataByDate(formattedDate);
  };

  const handlePreviousMonth = async () => {
    const formattedDate = dayjs(dateValue).tz(clientTimezone).subtract(1, 'month').format('YYYY-MM');
    setDateValue(formattedDate);
    await loadDataByDate(formattedDate);
  };

  // Efeito inicial para carregar dados
  useEffect(() => {
    let currentDate = dayjs().tz(clientTimezone);
    if (currentDate.date() < 20) currentDate = currentDate.subtract(1, 'month');
    const formattedDate = currentDate.format('YYYY-MM');
    setDateValue(formattedDate);

    const fetchData = async () => {
      try {
        const groupOptions = await loadGroupInputOptions();
        setSelectGroupOptions(groupOptions);

        const initialGroupFilter = isGroupResponsible ? responsibleGroupIds : [];
        const [year, month] = formattedDate.split('-');

        await Promise.all([
          loadWorkHappinessFeedbacks(1, year, month, { ageRanges: [], sexes: [], groups: initialGroupFilter }, true),
          loadFurtherFeelingsFeedbacks(1, year, month, { ageRanges: [], sexes: [], groups: initialGroupFilter }, true)
        ]);

        await loadData();
      } catch (e) {
        console.error(e);
        showToast(t('loadError'), 'error');
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientTimezone]);

  /* ---------- Return API of the hook ---------- */
  return {
    // Estados de loading
    loading,
    loadingWorkHappinessFeedbacksData,
    loadingFurtherFeelingsFeedbacksData,
    loadingSexesData,
    loadingAgeRangesData,
    loadingGroupsData,

    // Data e timezone
    dateValue, setDateValue, clientTimezone,

    // UI
    datePickerIsOpened,
    setDatePickerIsOpened,

    // Séries para gráficos
    dimensionsByFilters, genderSeries, ageRangesSeries, groupsSeries,

    // Dados
    generalData, filteredData,

    // Feedbacks
    workHappinessFeedbacksPageResults, furtherFeelingsFeedbacksPageResults,

    // Filtros
    representationSelectedOptions, setRepresentationSelectedOptions,
    appliedRepresentationSelectedOptions, appliedGroupOptions,
    selectGroupOptions, selectedGroupOptions, setSelectedGroups,

    // Permissões
    isGroupResponsible, responsibleGroupIds,
    companyCreatedAt,

    // Ações e helpers
    renderDate,
    loadData, loadFilteredData, loadDataByDate,
    handleNextMonth, handlePreviousMonth,
    loadWorkHappinessFeedbacks, loadFurtherFeelingsFeedbacks,
    clearFilteredData,
    debouncedLoadGroupInputOptions
  };
};
